/**
 * Pronoun helper — returns gendered pronouns based on cat sex.
 * Falls back to "they/their/them" when sex is unknown.
 *
 * Includes verb conjugation helpers (do/does, have/has) so that
 * template strings like "How comfortable do they seem?" can be
 * correctly conjugated for he/she/they.
 */

// The i18next singleton itself, not ../i18n: i18n.ts imports this module while it
// initialises.
import i18next from 'i18next';

export interface Pronouns {
  they: string;    // "she" / "he" / "they"
  their: string;   // "her" / "his" / "their"
  them: string;    // "her" / "him" / "them"
  theyre: string;  // "she's" / "he's" / "they're"
  They: string;    // capitalised
  Their: string;
  // Verb conjugation helpers for pronoun-verb agreement
  do: string;      // "does" / "does" / "do"
  have: string;    // "has" / "has" / "have"
  Do: string;      // capitalised
  Have: string;    // capitalised
}

export function getPronouns(sex: 'male' | 'female' | null | undefined): Pronouns {
  if (sex === 'female') {
    return { they: 'she', their: 'her', them: 'her', theyre: "she's", They: 'She', Their: 'Her', do: 'does', have: 'has', Do: 'Does', Have: 'Has' };
  }
  if (sex === 'male') {
    return { they: 'he', their: 'his', them: 'him', theyre: "he's", They: 'He', Their: 'His', do: 'does', have: 'has', Do: 'Does', Have: 'Has' };
  }
  return { they: 'they', their: 'their', them: 'them', theyre: "they're", They: 'They', Their: 'Their', do: 'do', have: 'have', Do: 'Do', Have: 'Have' };
}

/**
 * Returns the possessive form of a name, in the language the app is shown in —
 * the value of `{{catNamePossessive}}` / `{{catPossessive}}`.
 *
 * English: names ending in "s" get just an apostrophe ("Whiskers’", not
 * "Whiskers’s"); other names get "’s" ("Luna’s"). The sex is not used.
 * Portuguese: the preposition and article that agree with the cat — "da Luna"
 * (female), "do Tom" (male), "de Luna" (sex unknown). The pt-BR strings place the
 * value after its noun and never write "de" themselves ("o peso {{catNamePossessive}}").
 * Spanish: "de" and the name, with no article whatever the cat's sex ("de Luna",
 * "de Tom"); the es strings place it after the noun in the same way.
 *
 * The backend applies the same rule (src/i18n.py `possessive`).
 */
export function possessive(
  name: string,
  sex?: 'male' | 'female' | null,
  lang: string = i18next.language ?? 'en',
): string {
  if (!name) return '';
  if (/^pt\b/i.test(lang)) {
    const article = sex === 'female' ? 'da' : sex === 'male' ? 'do' : 'de';
    return `${article} ${name}`;
  }
  if (/^es\b/i.test(lang)) return `de ${name}`;
  return name.endsWith('s') || name.endsWith('S')
    ? `${name}’`
    : `${name}’s`;
}

/**
 * Upper-case the first letter, for a name or fallback that opens a sentence or a
 * heading: "your cat" → "Your cat". Mid-sentence, the fallback stays lower case.
 * Registered as the i18next `capitalize` format (`{{catName, capitalize}}`).
 */
export function capitalizeFirst(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/**
 * Apply pronoun substitution to a template string.
 * Replaces placeholder words (they/their/them/do/have) with the correct
 * gendered forms, preserving capitalisation.
 */
export function applyPronouns(template: string, p: Pronouns): string {
  return template
    .replace(/\bHave they\b/g, `${p.Have} ${p.they}`)
    .replace(/\bhave they\b/g, `${p.have} ${p.they}`)
    .replace(/\bHave their\b/g, `${p.Have} ${p.their}`)
    .replace(/\bhave their\b/g, `${p.have} ${p.their}`)
    .replace(/\bHas their\b/g, `${p.Have} ${p.their}`)
    .replace(/\bhas their\b/g, `${p.have} ${p.their}`)
    .replace(/\bDo they\b/g, `${p.Do} ${p.they}`)
    .replace(/\bdo they\b/g, `${p.do} ${p.they}`)
    .replace(/\bAre they\b/g, `${p.have === 'has' ? 'Is' : 'Are'} ${p.they}`)
    .replace(/\bare they\b/g, `${p.have === 'has' ? 'is' : 'are'} ${p.they}`)
    .replace(/\bThey're\b/g, p.theyre.charAt(0).toUpperCase() + p.theyre.slice(1))
    .replace(/\bthey're\b/g, p.theyre)
    .replace(/\btheir\b/g, p.their)
    .replace(/\bThem\b/g, p.them.charAt(0).toUpperCase() + p.them.slice(1))
    .replace(/\bthem\b/g, p.them)
    .replace(/\bThey\b/g, p.They)
    .replace(/\bthey\b/g, p.they);
}

/**
 * The i18next context for a cat's sex. A gendered sentence is one key written
 * out in full three times — `key` (sex unknown: they/their), `key_male`,
 * `key_female` — and passing `{ context: pronounContext(cat.sex) }` to `t()` picks one.
 * Translations need this rather than pronoun substitution: Portuguese marks
 * gender on adjectives and articles, not only on pronouns.
 */
export function pronounContext(sex: 'male' | 'female' | null | undefined): 'male' | 'female' | undefined {
  return sex === 'male' || sex === 'female' ? sex : undefined;
}
